import { motion } from 'framer-motion';
import { Star, Brain, Clock } from 'lucide-react';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import ExpertAvatar from './ExpertAvatar';
import type { AikupItem } from '../data/aikupData';

interface Leader {
  name: string;
  avatar?: string;
  title?: string;
  email?: string;
  departmentKey?: string;
}

interface AikupCardProps {
  item: AikupItem;
  onClick: () => void;
  onExpertClick: (expert: 'dany' | 'yannick' | 'francois') => void;
  index: number;
  customLeaders?: Leader[];
  onLeaderClick?: (leader: Leader) => void;
  stampText?: string; // ex: "APPROVED"
  hideCategoryBadge?: boolean;
  hideAIBadge?: boolean;
}

export default function AikupCard({ item, onClick, onExpertClick, index, customLeaders, onLeaderClick, stampText, hideCategoryBadge = false, hideAIBadge = false }: AikupCardProps) {
  const isCommunicationClient = item.id === 2;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      whileHover={{ scale: 1.02, y: -5 }}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Ouvrir ${item.title}`}
      className={`relative h-64 overflow-hidden rounded-lg bg-slate-800 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group ${
        isCommunicationClient ? 'ring-2 ring-bz-blue ring-opacity-60 shadow-bz-blue/20' : ''
      }`}
    >
      {/* Image de fond */}
      {item.imageUrl && (
        <div
          className="absolute inset-0 z-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
          style={{
            backgroundImage: `url(${item.imageUrl})`,
          }}
        />
      )}
      
      {/* Overlay gradient pour lisibilité */}
      <div
        className={`absolute inset-0 z-[1] transition-all duration-300 ${
          isCommunicationClient
            ? 'bg-gradient-to-t from-black/90 via-black/60 to-black/30 group-hover:from-bz-blue/95 group-hover:via-bz-blue/70'
            : 'bg-gradient-to-t from-black/90 via-black/60 to-black/30 group-hover:from-bz-blue/95 group-hover:via-bz-blue/70'
        }`}
      />
      
      {/* Badge Category en haut à gauche */}
      <div className="absolute top-2 left-2 z-10 flex flex-col gap-1.5">
        {!hideCategoryBadge && item.category && (
          <span className={`px-3 py-1 backdrop-blur-sm text-white text-xs font-medium rounded-full border transition-all duration-300 ${
            isCommunicationClient 
              ? 'bg-bz-blue/40 border-bz-blue/60 shadow-lg shadow-bz-blue/20' 
              : 'bg-white/20 border-white/30'
          }`}>
            {item.category}
          </span>
        )}

        {item.isInRevision && (
          <span className="px-3 py-1 backdrop-blur-sm text-white text-xs font-semibold rounded-full border bg-amber-500/30 border-amber-300/50 shadow-lg shadow-amber-500/20">
            Contenu en révision
          </span>
        )}
        
        {/* Brain icon si dans knowledge base */}
        {!hideAIBadge && item.inKnowledgeBase && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
            className="flex items-center gap-1 px-2 py-1 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full shadow-lg shadow-purple-500/20"
            title="Disponible dans AI Assistant"
          >
            <Brain className="w-3 h-3 text-purple-200" />
            <span className="text-xs text-purple-100 font-semibold">AI</span>
          </motion.div>
        )}
      </div>

      {/* Avatars des experts ou leaders en haut */}
      <div className="absolute top-2 right-2 z-10 flex gap-1.5">
        {customLeaders && customLeaders.length > 0 ? (
          // Afficher les leaders personnalisés
          customLeaders.filter((leader) => leader.avatar).map((leader, idx) => (
            <div
              key={idx}
              onClick={(e) => {
                e.stopPropagation();
                onLeaderClick?.(leader);
              }}
              className="cursor-pointer hover:scale-110 transition-transform duration-200 flex flex-col items-center"
              title={leader.title || leader.name}
            >
              <div className="w-10 h-10 rounded-full ring-2 ring-white/50 overflow-hidden shadow-lg">
                <img 
                  src={leader.avatar}
                  alt={leader.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="mt-1 text-[10px] font-semibold text-white drop-shadow text-center leading-none max-w-[52px] truncate">
                {leader.name}
              </span>
            </div>
          ))
        ) : (
          // Afficher les experts par défaut sous la gouvernance Dany CA/CE.
          <>
            <div 
              onClick={(e) => {
                e.stopPropagation();
                onExpertClick('dany');
              }}
              className="cursor-pointer hover:scale-110 transition-transform duration-200"
            >
              <ExpertAvatar name="Dany" score={item.scoreDany || 5} type="dany" />
            </div>
            <div 
              onClick={(e) => {
                e.stopPropagation();
                onExpertClick('francois');
              }}
              className="cursor-pointer hover:scale-110 transition-transform duration-200"
            >
              <ExpertAvatar name="François" score={item.scoreFrancois || 5} type="francois" />
            </div>
          </>
        )}
      </div>

      {/* Sceau / Stamp */}
      {stampText && (
        <motion.div
          className="absolute -bottom-1 -right-1 z-10 pointer-events-none"
          initial={{ opacity: 0, scale: 0.95, rotate: -12 }}
          animate={{
            opacity: [0.85, 1, 0.85],
            scale: [1, 1.03, 1],
          }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
        >
          <div className="relative">
            <div className="absolute inset-0 blur-md bg-emerald-400/30 rounded-full" />
            <div className="rounded-full border-4 border-emerald-300/90 bg-emerald-950/70 backdrop-blur-sm px-5 py-2 shadow-xl">
              <span className="text-[11px] tracking-[0.28em] font-extrabold text-emerald-100 drop-shadow">
                {stampText}
              </span>
            </div>
          </div>
        </motion.div>
      )}

      {/* Contenu */}
      <div className="relative z-[2] flex h-full flex-col p-6 pt-20 text-white">
        <div className="min-w-0 flex-1 overflow-hidden">
          <div className="mb-3 flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex items-start gap-2">
                <h3 className="text-xl font-bold leading-snug break-words drop-shadow-lg">{item.title}</h3>
              </div>
              {/* 5 étoiles pour Communication Client */}
              {isCommunicationClient && (
                <div className="flex items-center gap-1 mt-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <motion.div
                      key={star}
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.7, 1, 0.7],
                      }}
                      transition={{
                        duration: 1.5,
                        delay: star * 0.1,
                        repeat: Infinity,
                        repeatType: "reverse",
                        ease: "easeInOut"
                      }}
                    >
                      <Star 
                        className="w-4 h-4 text-yellow-400 fill-yellow-400 drop-shadow-lg" 
                      />
                    </motion.div>
                  ))}
                  <span className="text-xs text-yellow-300 font-bold ml-2 drop-shadow">
                    PREMIUM
                  </span>
                </div>
              )}
            </div>
          </div>
          <p className="line-clamp-4 text-sm leading-relaxed break-words text-white/90 drop-shadow">{item.description}</p>
          {item.lastSyncDate && (
            <p className="text-white/70 text-xs mt-2 flex items-center gap-1.5 drop-shadow">
              <Clock className="w-3.5 h-3.5" />
              Dernière synchro LEX: {formatLastAnalysisDate(item.lastSyncDate)}
            </p>
          )}
        </div>
      </div>
      
      {/* Effet de brillance au hover */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent" />
      </div>
      
      {/* Effet FLASH spécial pour Communication Client */}
      {isCommunicationClient && (
        <motion.div
          animate={{
            opacity: [0, 0.8, 0],
            scale: [1, 1.02, 1],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut"
          }}
          className="absolute inset-0 pointer-events-none"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-400/10 to-transparent" />
        </motion.div>
      )}
      
      {/* Bordure dorée animée pour Communication Client */}
      {isCommunicationClient && (
        <motion.div
          animate={{
            opacity: [0.4, 1, 0.4],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute inset-0 rounded-lg border-2 border-yellow-400 pointer-events-none shadow-lg shadow-yellow-400/30"
        />
      )}
      
      {/* Effet de particules dorées */}
      {isCommunicationClient && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              animate={{
                y: [0, -20, 0],
                opacity: [0, 1, 0],
                x: [0, Math.random() * 20 - 10, 0],
              }}
              transition={{
                duration: 2 + Math.random() * 2,
                delay: i * 0.3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute w-1 h-1 bg-yellow-400 rounded-full"
              style={{
                left: `${20 + i * 15}%`,
                top: `${80 + Math.random() * 20}%`,
              }}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
}
